import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { ICart, ICartItem } from "../models/ICart";
import { ISkuDetail } from "../models/ISkuDetail";
import { useAppSelector } from "../redux/features/redux-hooks";
import { getCartItems, updateCartItem } from "../api/CartAPI";
import { IDeliveryDetails } from "../models/IWallet";

const FREE_SHIPPING_AMOUNT = 35;

const initialState: ICart = {
  items: [],
  sub_total: 0,
  total_price: 0,
  id: 0,
  created_by: 0,
  updated_by: 0,
  deleted_by: 0,
  created_at: "",
  updated_at: "",
  deleted_at: "",
  user_id: 0,
  deliveryDetails: {},
};

interface CartContextType {
  cart: ICart;
  addToCart: ({ sku, quantity }: { sku: ISkuDetail; quantity?: number }) => void;
  removeFromCart: (item: ICartItem) => void;
  updateCartItemQuantity: ({ sku, quantity }: { sku: string; quantity: number }) => void;
  clearCart: () => void;
  totalPrice: number;
  totalQuantity: number;
  freeShippingBarPercentage: number;
  freeShippingText: string;
  mergeGuestCartWithUserCart: () => Promise<void>;
  deliveryDetails: IDeliveryDetails;
  setDeliveryDetails: (value: IDeliveryDetails | ((prev: IDeliveryDetails) => IDeliveryDetails)) => void;
}

const CartContext = createContext<CartContextType>({} as CartContextType);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { basicUserInfo } = useAppSelector((state) => state.authState);
  const [cart, setCart] = useState<ICart>(initialState);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [freeShippingBarPercentage, setFreeShippingBarPercentage] = useState(0);
  const [freeShippingText, setFreeShippingText] = useState("");
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  const [deliveryDetails, setDeliveryDetails] = useState<IDeliveryDetails>(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        return parsedCart.deliveryDetails || {
          online_order: false,
          delivery_date: '',
          delivery_time_from: '',
          delivery_time_to: '',
          self_collect: false,
          shipping_first_name: '',
          shipping_last_name: '',
          shipping_address: '',
          shipping_postal_code: '',
          shipping_phone_number: ''
        };
      } catch (e) {
        console.error("Failed to parse saved cart", e);
        return {
          online_order: false,
          delivery_date: '',
          delivery_time_from: '',
          delivery_time_to: '',
          self_collect: false,
          shipping_first_name: '',
          shipping_last_name: '',
          shipping_address: '',
          shipping_postal_code: '',
          shipping_phone_number: ''
        };
      }
    }
    return {
      online_order: false,
      delivery_date: '',
      delivery_time_from: '',
      delivery_time_to: '',
      self_collect: false,
      shipping_first_name: '',
      shipping_last_name: '',
      shipping_address: '',
      shipping_postal_code: '',
      shipping_phone_number: ''
    };
  });

  // Load cart from localStorage on initial render
  useEffect(() => {
    const initializeCart = async () => {
      try {
        if (basicUserInfo) {
          // Logged-in user: Get cart from server
          const serverCart = await getCartItems();
          
          // Add null check to ensure serverCart is valid
          if (serverCart && typeof serverCart === 'object') {
            setCart({
              ...initialState,
              ...serverCart,
              items: serverCart.items || []
            });
            
            // Load delivery details if available
            if (serverCart.deliveryDetails) {
              setDeliveryDetails(serverCart.deliveryDetails);
            }
          } else {
            console.error("Invalid cart data received from server:", serverCart);
            // Don't reset to initialState for server errors - keep existing cart
          }
        } else {
          // Guest user: Get cart from localStorage
          const savedCart = localStorage.getItem('cart');
          if (savedCart) {
            try {
              const parsedCart = JSON.parse(savedCart);
              
              // Make sure we have items as an array, even if empty
              const safeItems = Array.isArray(parsedCart.items) ? parsedCart.items : [];
              
              setCart({
                ...initialState,
                ...parsedCart,
                items: safeItems
              });
              
              // Load delivery details from cart if available
              if (parsedCart.deliveryDetails) {
                setDeliveryDetails(parsedCart.deliveryDetails);
              }
            } catch (parseError) {
              console.error("Error parsing saved cart:", parseError);
              // Don't reset to initialState for parse errors - keep the existing state
              // The cart might already have items from previous interactions
            }
          }
          // If no saved cart, we keep the initialState that was set in useState
        }
      } catch (error) {
        console.error("Error initializing cart:", error);
        // Don't reset to initialState for general errors - keep the existing state
      } finally {
        setInitialLoadComplete(true);
      }
    };

    initializeCart();
  }, [basicUserInfo]);

  // Save cart to localStorage and sync with server when cart changes
  useEffect(() => {
    // Only run after initial load is complete to prevent race conditions
    if (!initialLoadComplete) return;
    
    const syncCart = async () => {
      try {
        // Initialize cartData with deliveryDetails even if cart is empty
        const cartData = {
          items: cart?.items || [],
          sub_total: cart?.sub_total || 0,
          total_price: cart?.total_price || 0,
          deliveryDetails
        };
        
        // Always save to localStorage (for both guests and logged-in users)
        localStorage.setItem('cart', JSON.stringify(cartData));
        
        // For logged-in users, also update server
        if (basicUserInfo && cart?.items && cart.items.length > 0) {
          await updateCartItem({
            items: cart.items,
            sub_total: cart.sub_total,
            total_price: cart.total_price,
          });
        }
      } catch (error) {
        console.error("Error syncing cart:", error);
      }
    };
  
    syncCart();
  }, [cart, basicUserInfo, deliveryDetails, initialLoadComplete]);

  // Update derived states when cart changes
  useEffect(() => {
    // Add null check to prevent accessing .items on null cart
    if (!cart || !cart.items) {
      console.error("Invalid cart state detected in derived state calculation:", cart);
      return;
    }

    const newTotalPrice = cart.items.reduce(
      (sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0
    );
    const newTotalQuantity = cart.items.reduce(
      (sum, item) => sum + (item.quantity || 0), 0
    );

    setTotalPrice(newTotalPrice);
    setTotalQuantity(newTotalQuantity);

    // Free shipping calculation
    const amountToFreeShipping = FREE_SHIPPING_AMOUNT - newTotalPrice;
    setFreeShippingText(
      amountToFreeShipping > 0
        ? `Add $${amountToFreeShipping.toFixed(2)} more for free shipping!`
        : "You've earned free shipping!"
    );
    setFreeShippingBarPercentage(
      Math.min((newTotalPrice / FREE_SHIPPING_AMOUNT) * 100, 100)
    );
  }, [cart]);

  const calculateCartItemSubTotal = useCallback((cartItems: ICartItem[]) => {
    // Handle null or undefined cartItems
    if (!cartItems) {
      return 0;
    }
    return cartItems.reduce((acc, item) => acc + (item.price || 0) * (item.quantity || 0), 0);
  }, []);

  const updateCartState = useCallback((newItems: ICartItem[]) => {
    // Ensure newItems is an array
    const safeItems = Array.isArray(newItems) ? newItems : [];
    const sub_total = calculateCartItemSubTotal(safeItems);
    const total_price = sub_total;

    setCart(prev => ({
      ...prev,
      items: safeItems,
      sub_total,
      total_price,
    }));
  }, [calculateCartItemSubTotal]);

  const addToCart = useCallback(({ sku, quantity = 1 }: { sku: ISkuDetail; quantity?: number }) => {
    setCart(prev => {
      // Make sure prev.items is an array
      const currentItems = Array.isArray(prev.items) ? prev.items : [];
      const existingIndex = currentItems.findIndex(item => item.sku_sku === sku.sku_sku);
      const newItems = [...currentItems];

      if (existingIndex >= 0) {
        newItems[existingIndex].quantity += quantity;
      } else {
        newItems.push({
          sku_sku: sku.sku_sku,
          sku_name: sku.name || `Product ${sku.sku_sku}`,
          price: sku.price,
          quantity,
        });
      }

      return {
        ...prev,
        items: newItems,
        sub_total: calculateCartItemSubTotal(newItems),
        total_price: calculateCartItemSubTotal(newItems),
      };
    });
  }, [calculateCartItemSubTotal]);

  const removeFromCart = useCallback((item: ICartItem) => {
    // Make sure cart.items is an array
    if (!cart || !cart.items) {
      console.error("Cannot remove item from invalid cart:", cart);
      return;
    }
    updateCartState(cart.items.filter(i => i.sku_sku !== item.sku_sku));
  }, [cart, updateCartState]);

  const updateCartItemQuantity = useCallback(({ sku, quantity }: { sku: string; quantity: number }) => {
    // Make sure cart.items is an array
    if (!cart || !cart.items) {
      console.error("Cannot update quantity in invalid cart:", cart);
      return;
    }

    const newItems = [...cart.items];
    const existingIndex = newItems.findIndex(item => item.sku_sku === sku);

    if (existingIndex >= 0) {
      if (quantity <= 0) {
        newItems.splice(existingIndex, 1);
      } else {
        newItems[existingIndex].quantity = quantity;
      }
    }

    updateCartState(newItems);
  }, [cart, updateCartState]);

  const clearCart = useCallback(() => {
    setCart(initialState);
    localStorage.removeItem('cart');
  }, []);

  const mergeGuestCartWithUserCart = useCallback(async () => {
    if (!basicUserInfo) return;

    try {
      const userCart = await getCartItems();
      
      // Make sure userCart and cart.items are valid
      if (!userCart || !userCart.items) {
        console.error("Invalid user cart received:", userCart);
        return;
      }
      
      if (!cart || !cart.items) {
        console.error("Invalid local cart state:", cart);
        return;
      }
      
      const mergedItems = [...userCart.items];
      
      cart.items.forEach(guestItem => {
        const existingIndex = mergedItems.findIndex(item => item.sku_sku === guestItem.sku_sku);
        
        if (existingIndex >= 0) {
          mergedItems[existingIndex].quantity += guestItem.quantity;
        } else {
          mergedItems.push(guestItem);
        }
      });

      const sub_total = calculateCartItemSubTotal(mergedItems);
      const total_price = sub_total;

      const updatedCart = {
        ...userCart,
        items: mergedItems,
        sub_total,
        total_price,
      };

      await updateCartItem({
        items: mergedItems,
        sub_total,
        total_price,
      });

      setCart(updatedCart);
      localStorage.removeItem("cart");
    } catch (error) {
      console.error("Failed to merge guest cart:", error);
    }
  }, [basicUserInfo, cart, calculateCartItemSubTotal]);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateCartItemQuantity,
        clearCart,
        totalPrice,
        totalQuantity,
        freeShippingBarPercentage,
        freeShippingText,
        mergeGuestCartWithUserCart,
        deliveryDetails,
        setDeliveryDetails,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};